import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProductAdd from "./pages/ProductAdd";
import { StatisticsPage } from "./pages/StatisticsPage";
import RequireAuth from "./components/RequireAuth";
import ReturnProcessPage from "./pages/ReturnProcessPage";
import RefundInvoiceDetailsPage from "./pages/RefundInvoiceDetailsPage";

import UploadImage from './pages/UploadImage';
import SupplierPageWrapper from './pages/SupplierPage';
import SaleProductEntryForm from "./components/SaleProductEntryForm";
import InvoiceReportPage from "./pages/InvoiceReportPage";
import CustomerLedgerPage from "./pages/CustomerLedgerPage";
import BackButtonTest from "./components/BackButtonTest";
import SettingsPage from "./components/SettingsPage";
import CompanyManagementPage from "./pages/CompanyManagementPage";
import UserManagementPage from "./pages/UserManagementPage";
import CouponsManagementPage from "./pages/CouponsManagementPage";
import SupplierInvoiceProductEntry from "./components/SupplierInvoiceProductEntry";
import SupplierInvoicesPayments from "./components/SupplierInvoicesPayments";
import SupplierInvoicesPaymentsPage from "./pages/SupplierInvoicesPaymentsPage";
import { SalesInvoiceProvider } from "./context/SalesInvoiceContext";
import { KeyboardScrollProvider } from "./context/KeyboardScrollContext";
import { UserPrivilegesProvider } from "./context/UserPrivilegesContext";
import { useAndroidBackNavigation } from "./hooks/use-android-back-navigation";
import * as React from "react";
import { useEffect } from "react";
import { isWebPlatform, validateTokenForWeb } from "./lib/authToken";
import { initializeGlobal401Interceptor } from "./lib/global401Interceptor";

const queryClient = new QueryClient();

// Component to handle back navigation initialization for Android
const BackNavigationHandler = () => {
  useAndroidBackNavigation();
  return null;
};

// Component to handle web-specific token validation
const WebTokenValidator = () => {
  useEffect(() => {
    const initializeWebTokenValidation = async () => {
      if (isWebPlatform()) {
        console.log('Web platform detected, initializing token validation...');

        // Initialize global 401 interceptor
        initializeGlobal401Interceptor();

        try {
          const result = await validateTokenForWeb();
          console.log('Web token validation result:', { isValid: result.isValid, hasToken: !!result.token });
        } catch (error) {
          console.error('Web token validation error:', error);
        }
      }
    };

    initializeWebTokenValidation();
  }, []);

  return null;
};

const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <KeyboardScrollProvider>
        <SalesInvoiceProvider>
          <UserPrivilegesProvider>
          <TooltipProvider>
            <BackNavigationHandler />
            <WebTokenValidator />
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/product/add" element={<ProductAdd />} />
              <Route
                path="/reports/statistics"
                element={
                  <RequireAuth>
                    {(token) => <StatisticsPage token={token} />}
                  </RequireAuth>
                }
              />

              <Route
                path="/reports/invoice-report"
                element={
                  <RequireAuth>
                    {(token) => <InvoiceReportPage token={token} />}
                  </RequireAuth>
                }
              />
              <Route
                path="/reports/customer-ledger"
                element={
                  <RequireAuth>
                    {(token) => {
                      console.log('CustomerLedgerPage route matched, token:', token ? 'present' : 'missing');
                      return <CustomerLedgerPage token={token} />;
                    }}
                  </RequireAuth>
                }
              />
              <Route path="/upload-image" element={<UploadImage />} />
              <Route path="/suppliers" element={<SupplierPageWrapper />} />
              <Route path="/test/back-button" element={<BackButtonTest />} />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    {(token) => <SettingsPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/company"
                element={
                  <RequireAuth>
                    {(token) => <CompanyManagementPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/user-management"
                element={
                  <RequireAuth>
                    {(token) => <UserManagementPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/coupons"
                element={
                  <RequireAuth>
                    {(token) => <CouponsManagementPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/sale-product/add"
                element={
                  <RequireAuth>
                    {(token) => (
                      <SaleProductEntryForm
                        token={token}
                        onSuccess={() => {
                          // Let the component handle its own navigation
                          console.log('Product form onSuccess called');
                        }}
                        onCancel={() => {
                          // Let the component handle its own navigation
                          console.log('Product form onCancel called');
                        }}
                        showFooter={true}
                      />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path="/supplier-invoice-product-entry"
                element={
                  <RequireAuth>
                    {(token) => <SupplierInvoiceProductEntry token={token} />}
                  </RequireAuth>
                }
              />
              <Route
                path="/supplier-invoices-payments"
                element={
                  <RequireAuth>
                    {(token) => <SupplierInvoicesPaymentsPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/return-process"
                element={
                  <RequireAuth>
                    {(token) => <ReturnProcessPage />}
                  </RequireAuth>
                }
              />
              <Route
                path="/refunds/invoice"
                element={
                  <RequireAuth>
                    {(token) => <RefundInvoiceDetailsPage token={token} />}
                  </RequireAuth>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
          </UserPrivilegesProvider>
        </SalesInvoiceProvider>
      </KeyboardScrollProvider>
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;